'use client';

import type { DataGridProps } from '@mui/x-data-grid';
import { forwardRef } from 'react';

import { DataGrid, gridClasses } from '@mui/x-data-grid';

// ----------------------------------------------------------------------

/** Pointer + inherit cursor for nested content (use with row click / dense list rows). */
export const appDataGridClickableRowCursorSx = {
  [`& .${gridClasses.cell}`]: {
    cursor: 'pointer',
  },
  [`& .${gridClasses.row}`]: {
    cursor: 'pointer',
  },
  [`& .${gridClasses.row} .MuiDataGrid-checkboxInput`]: {
    cursor: 'pointer',
  },
  // Row uses `pointer`, but nested text/chips default to the text I-beam; inherit so the whole row reads as clickable.
  [`& .${gridClasses.row} .${gridClasses.cell} .MuiTypography-root`]: {
    cursor: 'inherit',
  },
  [`& .${gridClasses.row} .${gridClasses.cell} .MuiChip-root`]: {
    cursor: 'inherit',
  },
  [`& .${gridClasses.row} .${gridClasses.cell} svg`]: {
    cursor: 'inherit',
  },
} as const;

/** Activities list: 32px rows, horizontal cell padding, pointer rows (reference: `VisitDataGrid`). */
export const appDataGridListLayoutSx = {
  [`& .${gridClasses.cell}`]: {
    alignItems: 'center',
    display: 'inline-flex',
    padding: '0 10px',
    maxHeight: '32px !important',
    minHeight: '32px !important',
    ...appDataGridClickableRowCursorSx[`& .${gridClasses.cell}`],
  },
  [`& .${gridClasses.row}`]: {
    maxHeight: '32px !important',
    minHeight: '32px !important',
    ...appDataGridClickableRowCursorSx[`& .${gridClasses.row}`],
  },
  '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
    px: 1,
  },
  [`& .${gridClasses.row} .MuiDataGrid-checkboxInput`]: appDataGridClickableRowCursorSx[
    `& .${gridClasses.row} .MuiDataGrid-checkboxInput`
  ],
  [`& .${gridClasses.row} .${gridClasses.cell} .MuiTypography-root`]:
    appDataGridClickableRowCursorSx[`& .${gridClasses.row} .${gridClasses.cell} .MuiTypography-root`],
  [`& .${gridClasses.row} .${gridClasses.cell} .MuiChip-root`]:
    appDataGridClickableRowCursorSx[`& .${gridClasses.row} .${gridClasses.cell} .MuiChip-root`],
  [`& .${gridClasses.row} .${gridClasses.cell} svg`]:
    appDataGridClickableRowCursorSx[`& .${gridClasses.row} .${gridClasses.cell} svg`],
} as const;

/** Shared list / detail grid chrome (reference: activities `VisitDataGrid` + aligned list pages). */
export const appDataGridChromeSx = {
  '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
} as const;

export type AppDataGridProps = DataGridProps & {
  /**
   * Activities-style dense rows (32px, cell padding). Use `false` when the grid uses
   * `getRowHeight={() => 'auto'}`, custom `rowHeight`, or multi-line cells.
   */
  listLayout?: boolean;
};

/**
 * ScheduleAI standard wrapper around MUI X `DataGrid`.
 * Theme (`MuiDataGrid` overrides) owns selection / hover colors; this adds list chrome and optional list layout.
 * When `onRowClick` or `onRowDoubleClick` is set (without `listLayout`), pointer cursor is applied to rows/cells.
 */
export const AppDataGrid = forwardRef<HTMLDivElement, AppDataGridProps>(function AppDataGrid(
  { sx, listLayout = false, slotProps, onRowClick, onRowDoubleClick, ...props },
  ref
) {
  const applyClickableRowCursor =
    typeof onRowClick === 'function' || typeof onRowDoubleClick === 'function';

  return (
    <DataGrid
      ref={ref}
      slotProps={{
        ...slotProps,
        baseTooltip: { enterDelay: 1000, ...slotProps?.baseTooltip },
      }}
      onRowClick={onRowClick}
      onRowDoubleClick={onRowDoubleClick}
      {...props}
      sx={[
        appDataGridChromeSx,
        ...(listLayout ? [appDataGridListLayoutSx] : applyClickableRowCursor ? [appDataGridClickableRowCursorSx] : []),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    />
  );
});
